import { useEffect } from "react";

interface DocumentMeta {
  /** Page-specific part of the title. Omit on the home page. */
  title?: string;
  description?: string;
  /** Set to true on pages that shouldn't appear in search results. */
  noIndex?: boolean;
}

const SITE_NAME = "The Ghost";
const DEFAULT_DESCRIPTION =
  "The Ghost — a multiplayer horror game for iOS and Android. Survive together, or don't.";

const upsertMeta = (selector: string, attr: string, key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Keeps <title> and the social/SEO meta tags in sync with the current route.
 *
 * The app is a single-page build, so the static tags in `index.html` only ever
 * describe the home page. Every page calls this to correct them.
 */
export const useDocumentMeta = ({ title, description, noIndex }: DocumentMeta) => {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Multiplayer Horror`;
    const desc = description || DEFAULT_DESCRIPTION;

    document.title = fullTitle;

    upsertMeta('meta[name="description"]', "name", "description", desc);
    upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", desc);
    upsertMeta('meta[property="og:url"]', "property", "og:url", window.location.href);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", desc);
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noIndex ? "noindex, nofollow" : "index, follow"
    );
    upsertCanonical(window.location.href);
  }, [title, description, noIndex]);
};

export default useDocumentMeta;
